import React, {
    createContext,
    useContext,
    useMemo,
    useState
} from 'react';
import PropTypes from 'prop-types';

/* ========================================================================= */
/* Context(s) */
/* ========================================================================= */

const CarouselContext = createContext({
    swiper: null,
    swiperConfig: {},
    isPrevDisabled: false,
    isNextDisabled: false
});

/* ========================================================================= */
/* Hook(s) */
/* ========================================================================= */

/**
 * @hook useCarousel
 */
const useCarousel = () => {
    const context = useContext(CarouselContext);

    if (context === undefined) {
        throw new Error('useCarousel must be used within a CarouselProvider.');
    }

    return context;
};

/* ========================================================================= */
/* Provider(s) */
/* ========================================================================= */

/**
 * @component CarouselProvider
 * @props (see propTypes)
 */
const CarouselProvider = ({
    children,
    swiperConfig = {}
}) => {
    const [currentIndex, setCurrentIndex] = useState(false);
    const [isNextDisabled, setIsNextDisabled] = useState(false);
    const [isPrevDisabled, setIsPrevDisabled] = useState(false);
    const [swiper, setSwiper] = useState(null);
    const [totalSlides, setTotalSlides] = useState(0);

    const refreshSwiperStates = (_swiper) => {
        setCurrentIndex(_swiper.realIndex);
        setIsPrevDisabled(_swiper.isBeginning);
        setIsNextDisabled(_swiper.isEnd);
    };

    const config = {
        slidesPerView: 'auto',
        spaceBetween: 0,
        onTransitionEnd: refreshSwiperStates,
        onResize: refreshSwiperStates,
        onSwiper: (_swiper) => {
            setSwiper(_swiper);
            refreshSwiperStates(_swiper);
        },
        observer: true,
        observeParents: true,
        ...swiperConfig
    };

    return (
        <CarouselContext.Provider value={useMemo(() => {
            return {
                currentIndex,
                isNextDisabled,
                isPrevDisabled,
                refreshSwiperStates,
                totalSlides,
                setTotalSlides,
                swiper,
                swiperConfig: config
            };
        }, [
            config,
            currentIndex,
            isNextDisabled,
            isPrevDisabled,
            refreshSwiperStates,
            totalSlides,
            setTotalSlides,
            swiper,
            swiperConfig
        ])}>
            {children}
        </CarouselContext.Provider>
    );
};

CarouselProvider.propTypes = {
    children: PropTypes.any,
    swiperConfig: PropTypes.object
};

const CarouselContextConsumer = CarouselContext.Consumer;

/* ========================================================================= */
/* Export(s) */
/* ========================================================================= */

export {
    CarouselProvider,
    CarouselContextConsumer,
    useCarousel
};
